import BottomSheetContent from '../BottomSheetComponents/BottomSheetContent'
import { toPascalCaseWithSpaces } from '../../utils/utils'
import { LeafRank } from '../know-why/LeafRank'
import { PowerFormBadge } from '../ui-components/PowerFormBadge'
import { RegularTag } from '../ui-components/RegularTag'
import { PowerPerformanceForms } from '../../types/mutual-fund-detail.types'
import { mfLogoBaseUrl } from '../../env'
import Button from '../ui-components/Button'
import { openEliteLandingPage, trackEvent, onReady } from '../../utils/appBridge'
import { useEffect } from 'react'
import { EventNames } from '../../types/misc.types'
import RiveComponent, { RiveFit } from '../RiveComponent'

interface RegularToDirectBottomSheetProps {
  fundName: string
  regularFundValue: number
  onDismiss: () => void
  isRegular: boolean
  mfLogoUrl: string
  name: string
  fundNature: string
  fundCategory: string
  performanceRating: PowerPerformanceForms
  ScreenName?: string
}

/**
 * Bottom sheet component for Regular to Direct plan recommendation
 *
 * Displays a recommendation to switch from Regular to Direct plan
 * with padlock icon and tax-efficient messaging.
 */
export default function RegularToDirectBottomSheet({
  onDismiss,
  isRegular = true,
  mfLogoUrl,
  name,
  fundNature,
  fundCategory,
  performanceRating,
  ScreenName = 'REGULAR_TO_DIRECT_PLAN_PAGE'
}: RegularToDirectBottomSheetProps) {
  useEffect(() => {
    trackEvent({
      eventName: EventNames.KnowMoreBottomSheetLoad,
      payload: {
        eventId: 'RegularToDirectBottomSheet',
        ScreenName
      }
    })
    onReady()
  }, [ScreenName])

  return (
    <BottomSheetContent
      heading='Get a tax-efficient plan to switch to Direct funds'
      onDismiss={onDismiss}
      bgWhiteRequired={false}
      bottomSheetCtas={
        <Button
          buttonClassName='bg-black h-[56px] px-6 py-4 text-[16px] font-medium leading-[1.2] text-white'
          isFullWidth={true}
          onClick={() => {
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                buttonLabel: 'Unlock plan with Elite',
                eventId: 'UNLOCK_ELITE_BUTTON',
                screenType: ScreenName
              }
            })
            openEliteLandingPage()
          }}
        >
          Unlock plan with Elite
        </Button>
      }
    >
      <div className='flex flex-col gap-4'>
        <div className='border border-grey80 rounded-xl'>
          <div className='flex flex-row justify-center items-center w-full'>
            <div className={`h-10 w-10 self-start ml-5 mt-4 flex-shrink-0`}>
              <img className='h-full w-full object-contain' src={`${mfLogoBaseUrl}${mfLogoUrl}`} alt='know-why' />
            </div>
            <div className={`flex flex-col w-full pt-[18px] pb-5 min-h-[107px] ml-5`}>
              <div>
                <div>
                  <div className='flex justify-between'>
                    <div className='flex gap-4 w-1 flex-1'>
                      <div className='flex flex-col w-full'>
                        <div className='text-[13px] text-black w-10/12 leading-[140%]'>{name}</div>
                        <div className='pt-[2px] text-[13px] text-[#6A6E6E]'>
                          {toPascalCaseWithSpaces(fundNature)} / {toPascalCaseWithSpaces(fundCategory)}
                        </div>
                      </div>
                    </div>
                    <div></div>
                  </div>
                  <div className='flex items-center gap-2 pt-4'>
                    <LeafRank isElite={false} powerRank={null} powerForm={performanceRating as `${PowerPerformanceForms}`} />
                    <PowerFormBadge powerForm={performanceRating as `${PowerPerformanceForms}`} />
                    {isRegular && <RegularTag />}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='bg-[#FFF8ED] border border-[#FFDFB1] px-3 pt-4 pb-5 rounded-xl'>
          <div className='flex flex-row gap-3'>
            <div className='w-10 h-10 flex items-center justify-center flex-shrink-0'>
              <RiveComponent src='/rives/locked.riv' fit={RiveFit.FILL} className='w-full h-full' />
            </div>
            <div className='flex flex-col gap-[6px]'>
              <div className='text-sm font-medium text-orange30'>Regular to Direct recommendation</div>
              <div className='text-sm text-grey40'>
                Switch <span className='text-black'>₹**** of your lumpsum</span> to its Direct Plan to keep it
                tax-efficient
              </div>
            </div>
          </div>
        </div>
      </div>
    </BottomSheetContent>
  )
}
